import { prefersReducedMotion } from "@dashboard/utils/motion";
import { Box } from "@saleor/macaw-ui-next";
import React from "react";

const TRANSITION_MS = 220;

interface AnimatedHeightProps {
  children: React.ReactNode;
}

/**
 * Eases the container between the heights of its content instead of
 * snapping. Two moments need it: the dialog opening (grows from nothing to
 * the rows) and the out-of-stock skeleton being replaced by the fetched
 * rows. The container stays a flex child with min-height 0, so a taller
 * list still shrinks to the dialog's max height and scrolls.
 */
export const AnimatedHeight = ({ children }: AnimatedHeightProps) => {
  const content = React.useRef<HTMLDivElement>(null);
  // null until measured: "auto" would paint at full height and skip the
  // opening animation; 0 would hide the content wherever measuring is
  // unavailable, such as jsdom.
  const [height, setHeight] = React.useState<number | null>(null);
  const [animate] = React.useState(() => !prefersReducedMotion());
  // Overflow is hidden while the height eases, or a scrollbar flashes for
  // the duration of every grow. A timer rather than transitionend, which
  // never fires when the flex parent has already clamped the height.
  const [settling, setSettling] = React.useState(false);

  React.useEffect(() => {
    if (!animate || height === null) {
      return;
    }

    setSettling(true);

    const timer = window.setTimeout(
      () => setSettling(false),
      TRANSITION_MS + 50,
    );

    return () => window.clearTimeout(timer);
  }, [height, animate]);

  React.useEffect(() => {
    const el = content.current;

    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }

    const measure = () => setHeight(el.offsetHeight);
    const observer = new ResizeObserver(measure);

    observer.observe(el);
    // First paint happened at 0 (see the style below), so this measurement
    // is what the opening transition runs towards.
    measure();

    return () => observer.disconnect();
  }, []);

  const canMeasure = typeof ResizeObserver !== "undefined";

  return (
    <Box
      overflowY={settling ? "hidden" : "auto"}
      minHeight="0px"
      aria-live="polite"
      style={{
        height: height ?? (canMeasure ? 0 : "auto"),
        transition: animate ? `height ${TRANSITION_MS}ms ease` : undefined,
      }}
    >
      <div ref={content}>{children}</div>
    </Box>
  );
};
